import { Button, Flex, Input } from "antd";
import { useFormik } from "formik";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useDeleteMe } from "../../api/api.delete";
import {
  useGetProfile,
  useGetUserVerificationDetails,
} from "../../api/api.get";
import { useUpdateProfile } from "../../api/api.update";
import ConfirmationDialog from "../../components/dialog/confirmation.dialog.component";
import CountryDropdown from "../../components/dropdowns/country.dropdown.component";
import { useNotifications2 } from "../../components/notifications/notification";
import { useAuthContext } from "../../providers/auth.provider";
import { useLayoutContext } from "../../providers/layout.provider";
import { ROUTES, getCountryPhoneCode } from "../../utils/constants";
import { capitalize } from "../../utils/functions";
import AddressSection from "./profile/address-section";
import PhoneSection from "./profile/phone-section";

export default function ProfilePage() {
  const { user: userAuth } = useAuthContext();
  const { data, refetch } = useGetProfile();
  const { isMobile } = useLayoutContext();
  const [deleteConf, showDeleteConf] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { notification, notifySuccess, notifyError } = useNotifications2();

  useEffect(() => {
    if (userAuth) {
      refetch();
    }
  }, [userAuth]);

  const user = data;

  const navigate = useNavigate();

  const { mutateAsync: deleteUserAsync, isLoading: isDeleteLoading } =
    useDeleteMe();
  const { mutateAsync: updateProfileAsync, isLoading: isUpdateLoading } =
    useUpdateProfile();
  const { data: userVerificationDetails } = useGetUserVerificationDetails();
  const [verifiedProfile, setVerifiedProfile] = useState(false);

  useEffect(() => {
    if (userVerificationDetails) {
      setVerifiedProfile(
        userVerificationDetails.addressVerified &&
          userVerificationDetails.smsVerified
      );
    }
  }, [userVerificationDetails]);

  const formik = useFormik({
    initialValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      countryCode: user?.countryCode ?? "USA",
    },
    validationSchema: Yup.object({
      lastName: Yup.string().required("Please enter last name."),
      firstName: Yup.string().required("Please enter first name."),
    }),
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      // @ts-ignore
      delete values["consent"];

      updateProfileAsync({ ...values })
        .then(() => {
          notifySuccess({
            title: "Success",
            description: "Profile successfully saved.",
          });
        })
        .catch((err) => {
          if (err.response.status === 409) {
            notifyError(
              err.response.data?.message ?? "Whoops! Please try again"
            );
          } else {
            notifyError("Whoops! Something went wrong. Please try again.");
          }
        })
        .finally(() => setIsEditMode(false));
    },
  });
  const countryCode = useMemo(
    () => getCountryPhoneCode(formik.values.countryCode),
    [formik.values.countryCode]
  );

  const handleDeletion = () => {
    deleteUserAsync(undefined)
      .then(() => {
        navigate(ROUTES.ADMIN_USERS);
      })
      .catch(() => {
        notifyError("Whoops! Something went wrong. Please try again.");
      });
  };

  return (
    <div className="body">
      <ConfirmationDialog
        open={deleteConf}
        title={"Delete Account?"}
        heading="Are you sure?"
        subtitle="This will permanently delete all your data from all ExpenseHut products. Do you wish to continue?"
        onClose={() => showDeleteConf(false)}
        onProceed={handleDeletion}
      />
      {notification}

      <div className="mt-0">
        <div className="flex justify-between px-6 py-4 w-full bg-gray-100 mb-10">
          <Link
            to="/home"
            className="flex items-center gap-2 hover:opacity-80 transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
            <span className="text-black font-bold">Back</span>
          </Link>
        </div>

        <main className="max-w-4xl mx-auto px-6 py-2">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Profile Settings
            </h1>
            <p className="text-lg text-gray-600">
              Manage your personal information and business details
            </p>
          </div>

          <div className="my-4 p-6 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-700">
              Keep your profile information up to date to ensure you receive
              important notifications and updates about your account.
            </p>
          </div>

          {/* Profile Sections */}
          <div className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">
                  Personal Information
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Your basic personal details
                </p>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div className="space-y-2">
                    <span className="text-gray-700">First Name</span>
                    {isEditMode ? (
                      <Input
                        {...formik.getFieldProps("firstName")}
                        size="large"
                        status={!!formik.errors.firstName ? "error" : undefined}
                      />
                    ) : (
                      <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                        {capitalize(data?.firstName || "")}
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div className="space-y-2">
                    <span className="text-gray-700">Last Name</span>
                    {isEditMode ? (
                      <Input
                        {...formik.getFieldProps("lastName")}
                        size="large"
                        status={!!formik.errors.lastName ? "error" : undefined}
                      />
                    ) : (
                      <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                        {capitalize(data?.lastName || "")}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Email */}
                  <div className="space-y-2">
                    <span className="text-gray-700">Email Address</span>

                    <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                      {data?.email}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="text-gray-700">Country</span>
                    {isEditMode ? (
                      <div className="mx-0">
                        <CountryDropdown
                          onChange={(value) =>
                            formik.setFieldValue("countryCode", value)
                          }
                          value={formik.values.countryCode}
                        />
                      </div>
                    ) : (
                      <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border border-gray-300 text-gray-700">
                        {data?.countryCode}
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full flex justify-end">
                  {isEditMode && (
                    <Flex gap={30}>
                      <Button type="link" onClick={() => setIsEditMode(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={formik.submitForm}
                        id="update-profile"
                        loading={isUpdateLoading}
                      >
                        Save
                      </Button>
                    </Flex>
                  )}
                  {!isEditMode && (
                    <Button
                      id="edit-profile-btn"
                      type="link"
                      onClick={() => setIsEditMode(true)}
                    >
                      Edit Personal Info
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <PhoneSection areaCode={countryCode} />

          <AddressSection />

          <div className="">
            <hr className="my-10" />

            <div className="my-20 pb-10">
              <div
                style={{
                  border: "1px solid #ffb2b2",
                  background: "#ffb2b2",
                  borderRadius: 10,
                  color: "",
                }}
                className="p-4"
              >
                <strong>Danger Zone</strong>
                <p className="my-4 text-sm text-gray-700">
                  Deleting your account is irreversible. All your data will be
                  permanently removed from our servers. Please proceed with
                  caution.
                </p>
                <div className="my-6">
                  <Button
                    id="delete-account"
                    type="primary"
                    danger
                    loading={isDeleteLoading}
                    onClick={() => showDeleteConf(true)}
                  >
                    {" "}
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
        </main>
      </div>
    </div>
  );
}
